import React from "react";
import * as R from "ramda";
import { Icon, notification } from "antd";
import { makeAction } from "async-action-creator";
import download from "downloadjs";

import i18n from "../../i18n";
import history from "../../history";
import api from "../../utils/api";
import { createApiThunk } from "../../utils/thunk";

import { fetchOpportunities } from "./opportunities";
import { fetchTeams } from "./teams";

export const FETCH_COMPANY_PROFILE = makeAction("company/FETCH_PROFILE");
export const UPDATE_COMPANY_PROFILE = makeAction("company/UPDATE_PROFILE");
export const CONNECT_COMPANY_SOCIAL_ACCOUNT = makeAction(
  "company/CONNECT_COMPANY_SOCIAL_ACCOUNT"
);
export const DISCONNECT_COMPANY_SOCIAL_ACCOUNT = makeAction(
  "company/DISCONNECT_COMPANY_SOCIAL_ACCOUNT"
);
export const FETCH_COMPANY_USERS = makeAction("company/FETCH_COMPANY_USERS");
export const INVITE_COMPANY_USER = makeAction("company/INVITE_COMPANY_USER");
export const UPDATE_COMPANY_USER = makeAction("company/UPDATE_COMPANY_USER");
export const DELETE_COMPANY_USER = makeAction("company/DELETE_COMPANY_USER");
export const EXPORT_COMPANY_CANDIDATES = makeAction(
  "company/EXPORT_COMPANY_CANDIDATES"
);
export const SELECT_COMPANY = makeAction("company/SELECT_COMPANY");
export const ADD_COMPANY = makeAction("companies/ADD_COMPANY");

export const LINK_TO_COMPANIES = makeAction("company/LINK_TO_COMPANIES");

export const updateCompanyProfile = createApiThunk({
  action: UPDATE_COMPANY_PROFILE,
  request: ({ companyId, formData }) => {
    const companyData = api.company.update({ ...formData, id: companyId });

    return companyData;
  },
  onResolved: ({ getState }) => {
    const { user, company } = getState();
    const locale = user.locale === "" ? company.country : user.locale;

    if (locale) {
      i18n.changeLanguage(locale);
    }

    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:company.updateProfileResolved")
    });
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:company.updateProfileRejected")
    })
});

export const fetchCompanyProfile = createApiThunk({
  action: FETCH_COMPANY_PROFILE,
  request: async id => {
    const [profile, users] = await Promise.all([
      api.company.fetch(id),
      api.company.getUsers(id)
    ]);

    return {
      ...profile,
      users
    };
  },
  onResolved({ dispatch }) {
    dispatch(fetchOpportunities());
    dispatch(fetchTeams());
  }
});

export const inviteCompanyUser = createApiThunk({
  action: INVITE_COMPANY_USER,
  rejectOnError: true,
  request: async (inviteData, { company, headquarter }) => {
    const role = R.pipe(
      R.prop("roles"),
      R.head
    )(inviteData);
    const id = role === "headadmin" ? headquarter.id : company.id;

    return api.company.inviteUser(id, inviteData);
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("toasts:company.inviteUserResolved"),
      icon: <Icon type="mail" style={{ color: "#58c621ad" }} />
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:company.inviteUserRejectedDescription")
    })
});

export const updateCompanyUser = createApiThunk({
  action: UPDATE_COMPANY_USER,
  request: async ({ userId, ...formData }, { company }) =>
    api.user.update({
      company: company.id,
      user: userId,
      ...formData
    })
});

export const deleteCompanyUser = createApiThunk({
  action: DELETE_COMPANY_USER,
  request: async (userId, { company }) =>
    api.company.deleteUser(company.id, userId)
});

export const exportCompanyCandidates = createApiThunk({
  action: EXPORT_COMPANY_CANDIDATES,
  request: async id => {
    const data = await api.company.exportCandidates(id);
    return download(data, "candidates.csv", "text/plain");
  }
});

export const selectCompany = (company, url = "/oportunidades") => dispatch => {
  dispatch(SELECT_COMPANY.run(company));

  history.push(url);

  dispatch(fetchCompanyProfile(company.id));

  localStorage.setItem("selected_company_id", company.id);
};

export const addCompany = createApiThunk({
  action: ADD_COMPANY,
  request: (companyData, { user }) =>
    api.headquarter.company.list.addCompany(user.idCompany, companyData),
  onResolved: ({ params: { closeModal } }) => {
    closeModal();
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:company.addCompanyResolved")
    });
  },
  onRejected: ({ error, params: { onExistingUserError } }) => {
    const errorMessage = R.path(["response", "data", "message"], error);

    if (errorMessage === "user already exists") {
      onExistingUserError();
    } else {
      notification.error({
        message: i18n.t("global:error"),
        description: i18n.t("toasts:company.addCompanyRejected")
      });
    }
  }
});

export const connectCompanySocialAccount = createApiThunk({
  action: CONNECT_COMPANY_SOCIAL_ACCOUNT,
  request: ({ provider, ...options }, { company }) =>
    api.company.social.create(company.id, provider, options),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:ready"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsResolved")
    }),
  onRejected: error => {
    const status = R.path(["response", "status"], error);
    return notification.error({
      message: i18n.t("global:error"),
      description:
        status === 400
          ? i18n.t("toasts:company.updateCompanySocialAccountsExists")
          : i18n.t("toasts:company.updateCompanySocialAccountsRejected")
    });
  }
});

export const disconnectCompanySocialAccount = createApiThunk({
  action: DISCONNECT_COMPANY_SOCIAL_ACCOUNT,
  request: (provider, { company }) =>
    api.company.social.delete(company.id, provider),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:ready"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsRejected")
    })
});

export const linkToCompanies = createApiThunk({
  action: LINK_TO_COMPANIES,
  rejectOnError: true,
  request: (vinculations = [], { company }) => {
    return api.company.linkCompanies(company.id, vinculations);
  },
  onRejected: () => {
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:company.acceptLinkRejected")
    });
  }
});
